import { Download, HelpCircle, Languages, MessageSquare, Search, Settings } from 'lucide-react';
import { useChatModel, CHAT_MODEL_OPTIONS } from '@/hooks/useChatModel';

import { useChatFontSize, FONT_SIZE_OPTIONS } from '@/hooks/useChatFontSize';
import {
  useResponseDetailLevel,
  RESPONSE_DETAIL_OPTIONS,
  type ResponseDetailLevel,
} from '@/hooks/useResponseDetailLevel';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface ChatSettingsMenuProps {
  iconOnly?: boolean;
  onSearch: () => void;
  onExport: () => void;
  onLanguage: () => void;
  onHelp: () => void;
  onFeedback: () => void;
}

export function ChatSettingsMenu({
  iconOnly = false,
  onSearch,
  onExport,
  onLanguage,
  onHelp,
  onFeedback,
}: ChatSettingsMenuProps) {
  // `options` is the list as THIS account sees it — what is on request is disabled
  // in it. The static CHAT_MODEL_OPTIONS below is only for looking a label up, and
  // rendering the picker from that was what kept every restricted model greyed out
  // even for an account that may use it.
  const { chatModel, setChatModel, options } = useChatModel();
  const { chatFontSize, setChatFontSize } = useChatFontSize();
  const { responseDetailLevel, setResponseDetailLevel } = useResponseDetailLevel();
  const modelLabel =
    CHAT_MODEL_OPTIONS.find(option => option.value === chatModel)?.label || chatModel;
  const detailValue =
    RESPONSE_DETAIL_OPTIONS.find(option => option.value === responseDetailLevel)?.value || 'medium';
  const detailLabel =
    RESPONSE_DETAIL_OPTIONS.find(option => option.value === detailValue)?.label || 'Medium';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={iconOnly ? 'ghost' : 'outline'}
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs"
          aria-label={`Chat settings. Model: ${modelLabel}. Detail: ${detailLabel}.`}
          title={`Chat settings · ${modelLabel} · ${detailLabel}`}
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!iconOnly && <span className="max-w-28 truncate">{modelLabel}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Model<span className="ml-auto pr-2 text-xs text-slate-500">{modelLabel}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-64">
            <DropdownMenuRadioGroup
              value={chatModel}
              onValueChange={value => setChatModel(value as typeof chatModel)}
            >
              {options.map(option => (
                <DropdownMenuRadioItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  <div>
                    <div>{option.label}</div>
                    {/* A locked model still says what it is, then how to ask for it —
                        "Not available" told the reader nothing they could act on. */}
                    <p className="text-xs text-slate-500">{option.description}</p>
                    {option.requestNote && (
                      <p className="text-xs text-amber-700">{option.requestNote}</p>
                    )}
                  </div>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Response detail
            <span className="ml-auto pr-2 text-xs text-slate-500">{detailLabel}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-64">
            <DropdownMenuRadioGroup
              value={detailValue}
              onValueChange={value => setResponseDetailLevel(value as ResponseDetailLevel)}
            >
              {RESPONSE_DETAIL_OPTIONS.map(option => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  <div>
                    <div>{option.label}</div>
                    <p className="text-xs text-slate-500">{option.description}</p>
                  </div>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Font size</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={chatFontSize}
              onValueChange={value => setChatFontSize(value as typeof chatFontSize)}
            >
              {FONT_SIZE_OPTIONS.map(option => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={onLanguage}>
          <Languages className="mr-2 h-4 w-4" />
          Language
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSearch}>
          <Search className="mr-2 h-4 w-4" />
          Search conversation
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onExport}>
          <Download className="mr-2 h-4 w-4" />
          Download conversation
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onHelp}>
          <HelpCircle className="mr-2 h-4 w-4" />
          How chat works
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onFeedback}>
          <MessageSquare className="mr-2 h-4 w-4" />
          Send feedback
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
